import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { SidebarNavItem } from './types';

const DEFAULT_STORAGE_KEY = 'sidebar-pinned-order';

interface PinState {
  pinnedIds: string[];
}

const readStorage = (key: string): PinState | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as PinState;
  } catch {
    return null;
  }
};

const writeStorage = (key: string, state: PinState) => {
  localStorage.setItem(key, JSON.stringify(state));
};

const getDefaultPinnedIds = (items: SidebarNavItem[]): string[] =>
  items.filter((i) => i.id && i.compactGroup === 'primary').map((i) => i.id!);

export const useSidebarPins = (
  allItems: SidebarNavItem[],
  storageKey: string = DEFAULT_STORAGE_KEY
) => {
  const itemsWithId = useMemo(() => allItems.filter((i) => i.id), [allItems]);
  const defaultPinned = useMemo(() => getDefaultPinnedIds(allItems), [allItems]);
  const hasHydratedRef = useRef(false);

  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    const stored = readStorage(storageKey);
    if (stored && stored.pinnedIds.length > 0) {
      hasHydratedRef.current = true;
      return stored.pinnedIds;
    }
    return defaultPinned;
  });

  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (itemsWithId.length === 0) return;

    const stored = readStorage(storageKey);
    if (stored && stored.pinnedIds.length > 0) {
      setPinnedIds(stored.pinnedIds);
    } else {
      setPinnedIds(defaultPinned);
    }
    hasHydratedRef.current = true;
  }, [itemsWithId.length, defaultPinned, storageKey]);

  const persist = useCallback(
    (ids: string[]) => {
      setPinnedIds(ids);
      writeStorage(storageKey, { pinnedIds: ids });
    },
    [storageKey]
  );

  const pinnedItems = useMemo(() => {
    const byId = new Map(itemsWithId.map((i) => [i.id, i]));
    return pinnedIds.map((id) => byId.get(id)).filter(Boolean) as SidebarNavItem[];
  }, [pinnedIds, itemsWithId]);

  const unpinnedItems = useMemo(() => {
    const pinSet = new Set(pinnedIds);
    return itemsWithId.filter((i) => !pinSet.has(i.id!));
  }, [pinnedIds, itemsWithId]);

  const pin = useCallback(
    (id: string) => {
      if (pinnedIds.includes(id)) return;
      persist([...pinnedIds, id]);
    },
    [pinnedIds, persist]
  );

  const unpin = useCallback(
    (id: string) => {
      persist(pinnedIds.filter((pid) => pid !== id));
    },
    [pinnedIds, persist]
  );

  const moveUp = useCallback(
    (id: string) => {
      const idx = pinnedIds.indexOf(id);
      if (idx <= 0) return;
      const next = [...pinnedIds];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      persist(next);
    },
    [pinnedIds, persist]
  );

  const moveDown = useCallback(
    (id: string) => {
      const idx = pinnedIds.indexOf(id);
      if (idx < 0 || idx >= pinnedIds.length - 1) return;
      const next = [...pinnedIds];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      persist(next);
    },
    [pinnedIds, persist]
  );

  const resetToDefaults = useCallback(() => {
    persist(defaultPinned);
  }, [defaultPinned, persist]);

  const pinAll = useCallback(() => {
    const allIds = itemsWithId.map((item) => item.id!);
    const pinnedSet = new Set(pinnedIds);
    persist([
      ...pinnedIds.filter((id) => allIds.includes(id)),
      ...allIds.filter((id) => !pinnedSet.has(id)),
    ]);
  }, [itemsWithId, pinnedIds, persist]);

  const unpinAll = useCallback(() => {
    persist([]);
  }, [persist]);

  return {
    pinnedItems,
    unpinnedItems,
    pinnedIds,
    pin,
    unpin,
    moveUp,
    moveDown,
    resetToDefaults,
    pinAll,
    unpinAll,
  };
};
